import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import siteConfigService from '../services/siteConfigService';
import { uploadToS3 } from '../services/s3UploadService';

// To make more content editable: add the field here and in
// server/features/site-config/fields.js, then read it in the affiliate app.
const SECTIONS = [
  {
    label: 'About page',
    description: 'Shown in the "About" sheet on public blog pages. Leave a field blank to use the built-in default.',
    fields: [
      { name: 'aboutDescription', label: 'Description', multiline: true, maxLength: 1000 },
      { name: 'aboutBrandsIntro', label: 'Message for brands', multiline: true, maxLength: 500 },
      { name: 'aboutContactEmail', label: 'Brand contact email', maxLength: 254 },
      { name: 'aboutInfluencersCta', label: 'Message for influencers / creators', multiline: true, maxLength: 500 }
    ]
  },
  {
    label: 'Thank-you page',
    description: 'Shown after an affiliate finishes signing up. Line breaks are kept. Leave blank to use the default.',
    fields: [
      { name: 'thankYouMessage', label: 'Message', multiline: true, maxLength: 500 }
    ]
  }
];

const ICON_SECTION = {
  label: 'Bottom bar icons',
  description: 'Icons in the bottom bar of public blog pages. Upload a PNG with a transparent background (square works best). Remove an icon to go back to the default.',
  fields: [
    { name: 'iconLogo', label: 'Logo (opens About)', type: 'image' },
    { name: 'iconComment', label: 'Comment', type: 'image' },
    { name: 'iconJoin', label: 'Join / make vids', type: 'image' },
    { name: 'iconShare', label: 'Send it (share)', type: 'image' },
    { name: 'iconNextTopic', label: 'Next topic (new idea)', type: 'image' }
  ]
};
SECTIONS.push(ICON_SECTION);

const MAX_ICON_BYTES = 2 * 1024 * 1024;

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);

const SiteConfig = () => {
  const [tab, setTab] = useState(0);
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingField, setUploadingField] = useState('');

  const toFormValues = (config) =>
    ALL_FIELDS.reduce((out, f) => ({ ...out, [f.name]: config[f.name] || '' }), {});

  useEffect(() => {
    siteConfigService
      .getConfig()
      .then((config) => {
        const form = toFormValues(config);
        setValues(form);
        setSaved(form);
      })
      .catch((err) => setError(err.response?.data?.error || 'Failed to load config'))
      .finally(() => setLoading(false));
  }, []);

  const section = SECTIONS[tab];
  const dirty = section.fields.some((f) => values[f.name] !== saved[f.name]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = section.fields.reduce((out, f) => ({ ...out, [f.name]: values[f.name] }), {});
      const config = await siteConfigService.updateConfig(payload);
      const form = toFormValues(config);
      setValues((prev) => ({ ...prev, ...section.fields.reduce((o, f) => ({ ...o, [f.name]: form[f.name] }), {}) }));
      setSaved((prev) => ({ ...prev, ...section.fields.reduce((o, f) => ({ ...o, [f.name]: form[f.name] }), {}) }));
      setSuccess(`${section.label} saved`);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleIconFile = async (name, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file');
      return;
    }
    if (file.size > MAX_ICON_BYTES) {
      setError('Image must be under 2MB');
      return;
    }
    setError('');
    setSuccess('');
    setUploadingField(name);
    try {
      const url = await uploadToS3(file, 'site-config');
      setValues((prev) => ({ ...prev, [name]: url }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploadingField('');
    }
  };

  const handleTabChange = (_, next) => {
    setTab(next);
    setError('');
    setSuccess('');
  };

  return (
    <DashboardLayout title="Config">
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Edit the content shown in the affiliate site for this instance.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              {SECTIONS.map((s) => (
                <Tab key={s.label} label={s.label} />
              ))}
            </Tabs>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {section.description}
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

              {section.fields.map((f) => f.type === 'image' ? (
                <Box key={f.name} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      flexShrink: 0,
                      bgcolor: '#000',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {values[f.name] ? (
                      <img src={values[f.name]} alt={f.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: '#fff' }}>Default</Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{f.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {values[f.name] ? 'Custom icon' : 'Using the built-in icon'}
                    </Typography>
                  </Box>
                  <Button component="label" variant="outlined" size="small" disabled={!!uploadingField}>
                    {uploadingField === f.name ? <CircularProgress size={18} /> : values[f.name] ? 'Replace' : 'Upload'}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        handleIconFile(f.name, e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  {values[f.name] && (
                    <Button size="small" color="error" onClick={() => setValues({ ...values, [f.name]: '' })} disabled={!!uploadingField}>
                      Remove
                    </Button>
                  )}
                </Box>
              ) : (
                <TextField
                  key={f.name}
                  fullWidth
                  label={f.label}
                  value={values[f.name] || ''}
                  onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  multiline={f.multiline}
                  minRows={f.multiline ? 3 : undefined}
                  inputProps={{ maxLength: f.maxLength }}
                  helperText={`${(values[f.name] || '').length}/${f.maxLength}`}
                  sx={{ mb: 3 }}
                />
              ))}

              <Button variant="contained" onClick={handleSave} disabled={saving || !!uploadingField || !dirty}>
                {saving ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </CardContent>
          </Card>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default SiteConfig;
